import React, { useEffect, useRef, useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Box,
  useTheme,
} from '@mui/material';

import clsx from 'clsx';

import { ContactUsForm } from './ContactUsForm';
import { ReactComponent as BookIcon } from '../../assets/icons/info/book.svg';
import { ReactComponent as HeadSetIcon } from '../../assets/icons/info/headset.svg';
import GetStarted from '../../components/GetStarted';
import { FaqAccordionItem } from '../../components/FaqAccordionItem/FaqAccordionItem';
import { useNavigate } from 'react-router-dom';

declare global {
  interface Window {
    zE: any;
    zESettings: any;
    $zopim: any;
  }
}
const shadowDefault =
  '-2.82px -2.82px 8.49px 0px #FFFFFF4D, ' +
  '14.15px 14.15px 22.64px -0.37px #0000001A, ' +
  '-17.54px -17.54px 28.66px 0px #FFFFFF0D, ' +
  '10px 10px 28.66px -11.7px #00000033, ' +
  '1.58px 1.58px 1.7px 0px #FFFFFF inset, ' +
  '-2.07px -2.64px 4.52px -1.51px #0000004D inset, ' +
  '0px 0px 0px 0px #00000000 inset, ' +
  '0px 0px 0px 0px #00000000, ' +
  '0px 0px 0px 0px #00000000';

const shadowActive =
  '0px 0px 0px 0px #00000000, ' +
  '0px 0px 0px 0px #00000000, ' +
  '0px 0px 0px 0px #00000000, ' +
  '0px 0px 0px 0px #00000000, ' +
  '14.15px 14.15px 22.64px -0.37px #00FFFF21 inset, ' +
  '-17.54px -17.54px 28.66px 0px #A6FFFF4D inset, ' +
  '10px 10px 28.66px -11.7px #00929233 inset, ' +
  '1.58px 1.58px 1.7px 0px #A3FFFF, ' +
  '-3.07px -4px 2px -1.51px #00929238';

const shadowHover =
  '0px 0px 0px 0px #00000000, ' +
  '0px 0px 0px 0px #00000000, ' +
  '0px 0px 0px 0px #00000000, ' +
  '0px 0px 0px 0px #00000000, ' +
  '1.58px 1.58px 1.7px 0px #A3FFFF inset, ' +
  '-2.07px -2.64px 4.52px -1.51px #00929252 inset, ' +
  '0px 0px 0px 0px #00000000 inset, ' +
  '14.15px 14.15px 22.64px -0.37px #00FFFF21, ' +
  '-17.54px -17.54px 28.66px 0px #A6FFFF30';

const SupportPage: React.FC = () => {
  const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
  const [isFormOpened, setIsFormOpened] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    // Check if we need to show the launcher
    showZendeskLauncherIfNeeded();
  }, []);

  // Function to show Zendesk launcher if user has previously engaged
  const showZendeskLauncherIfNeeded = () => {
    const hasEngagedWithSupport =
      localStorage.getItem('hasEngagedWithSupport') === 'true';

    if (hasEngagedWithSupport) {
      // Show the launcher after a short delay to ensure Zendesk is loaded
      setTimeout(() => {
        const zendeskLauncher = document.querySelector(
          'div[data-testid="launcher"]',
        ) as HTMLElement;
        if (zendeskLauncher) {
          zendeskLauncher.style.display = 'block';
        }
      }, 1000);
    }
  };

  // Function to open Zendesk widget
  const openZendeskChat = () => {
    if (window.zE) {
      // Mark that user has engaged with support
      localStorage.setItem('hasEngagedWithSupport', 'true');

      // Show the launcher for future visits
      const zendeskLauncher = document.querySelector(
        'div[data-testid="launcher"]',
      ) as HTMLElement;
      if (zendeskLauncher) {
        zendeskLauncher.style.display = 'block';
      }

      // Using new Web SDK API
      window.zE('messenger', 'open');
    }
  };

  const targetFAQ = useRef<HTMLDivElement | null>(null);
  // Support action cards
  const supportActions = [
    {
      title: 'Contact Support',
      className: true,
      description: 'Get help from our team via email.',
      icon: <HeadSetIcon />,
      action: () => {
        setIsFormOpened(true);
      },
    },
    {
      title: 'Help Blog',
      className: false,
      description: 'Find answers to common questions.',
      icon: <BookIcon />,
      action: () => {
        // const y =
        //   targetFAQ.current!.getBoundingClientRect().top +
        //   window.pageYOffset -
        //   80; //  header offset;

        // window.scrollTo({ top: y, behavior: 'smooth' });
        navigate('/help-blog');
      }, // This could be different in the future
    },
    // {
    //   title: 'Support Articles FAQ',
    //   description: 'Browse our knowledge base of helpful articles',
    //   icon: (
    //     <ArticleIcon sx={{ fontSize: 60, color: theme.palette.primary.main }} />
    //   ),
    //   action: () => openZendeskChat(), // This could be different in the future
    // },
  ];

  // FAQ items
  const faqItems = [
    {
      question: 'How do I create a new order?',
      answer:
        'Simply tap the "Create New Order" button on your dashboard. Choose a category, describe your task, and set a date. We’ll help you find the perfect professional for the job.',
    },
    {
      question: 'How do I message a handyman?',
      answer: `You can chat with a professional once you've selected them for your task or sent them a proposal. Go to "My Tasks", open the specific order, and click the "Chat" button to discuss details.`,
    },
    {
      question: 'How do I leave a review?',
      answer:
        'Once a task is marked as Completed, a "Leave a Review" button will appear on the Order Details page. You can rate your experience from 1 to 5 stars and write a comment to help other users.',
    },
    {
      question: 'How do I update my profile?',
      answer: `Click on your name in the header to open your Profile page. From there, you can edit your personal details, change your photo, and update your bio. Don't forget to save your changes.`,
    },
    {
      question: 'What payment methods do you accept?',
      answer:
        'We accept all major credit and debit cards (Visa, Mastercard, American Express). All payments are securely processed via Stripe once the task is completed.',
    },
    {
      question: 'When will I be charged?',
      answer:
        'You are only charged after the task is completed. When you hire a professional, we place a temporary hold (authorization) on your card to secure the funds, but the actual charge happens only when the work is done.',
    },
    {
      question: 'Can I cancel a task?',
      answer:
        'Yes, you can cancel a task from the Order Details page. If you cancel more than 24 hours before the scheduled time, there are no fees. Late cancellations may be subject to a fee to compensate the provider for their time.',
    },
    {
      question: 'Are the professionals verified?',
      answer:
        'Yes. We take safety seriously. All providers on Womapp undergo an identity verification process (KYC) before they can start accepting tasks on the platform.',
    },
    {
      question: 'What if I’m not satisfied with the work?',
      answer: `Your satisfaction is our priority. If the work wasn't done as agreed, please do not release the payment and click "Report an Issue" on the Order Details page. Our support team will help resolve the situation.`,
    },
    {
      question: 'Why do I need to pay through the app?',
      answer:
        'Paying through Womapp ensures your money is safe. It allows us to protect you with secure transactions, customer support, and dispute resolution. Payments made outside the app are not covered by our protection policies.',
    },
    {
      question: 'How do I delete my account?',
      answer:
        'To delete your account, please contact our Support Team. We will process your request and permanently remove your account and data within 48 hours.',
    },
    {
      question: 'How do I request a refund?',
      answer:
        'To request a refund, please contact our Support Team directly. We review all requests individually and will process your refund within 48 hours if eligible.',
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: isDark ? '#1A1A1A' : '#E5E5DC',
        paddingTop: { xs: '80px', sm: '120px' },
        paddingBottom: '60px',
      }}
    >
      <Container maxWidth='lg' sx={{ width: '956px', maxWidth: '100%' }}>
        {/* Page Title */}
        <Typography
          variant='h4'
          component='h1'
          sx={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: { xs: 600, sm: 500 },
            fontSize: { xs: '34px', sm: '60px' },
            color: isDark ? '#00FFFF' : '#005CAD',
            lineHeight: { xs: '124%', sm: '120%' },
            letterSpacing: { xs: '0.25px', sm: '-.5px' },
            marginBottom: { xs: '16px', sm: '24px' },
            marginTop: { xs: '16px', sm: '16px' },
            textAlign: 'center',
          }}
        >
          Help Center
        </Typography>
        <Typography
          variant='body1'
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: { xs: 14, sm: 16 },
            fontWeight: 700,
            letterSpacing: { xs: '.1px', sm: '.15px' },
            lineHeight: { xs: '157%', sm: '175%' },
            color: isDark ? '#B6B6B6' : '#1A1A1A',
            textAlign: 'center',
            marginBottom: { xs: '24px', sm: '60px' },
          }}
        >
          Get the help you need with our comprehensive support resources and
          direct assistance options.
        </Typography>
        {/* Support Actions Section */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: { xs: '16px', sm: '32px' },
            width: '100%',
            background: isDark ? '#1A1A1A' : '#E5E5DC',
            marginBottom: '48px',
          }}
        >
          {supportActions.map((action, index) => (
            <Card
              key={`${action.title}-${index}`}
              className={clsx({ active: isFormOpened && action.className })}
              sx={{
                padding: { xs: '24px 24px 38px', sm: '32px 32px 48px' },
                borderRadius: { xs: '32px', sm: '64px' },
                width: { xs: '194px', sm: '244px' },
                border: 0,
                boxShadow: shadowDefault,
                transition: ' box-shadow 0.3s',
                '& ': {
                  background: isDark ? '#1a1a1a' : 'rgba(229, 229, 220, 1)',
                },
                '&:hover': {
                  boxShadow: shadowHover,
                },
                '&.active': {
                  boxShadow: shadowActive,
                },
              }}
            >
              <CardActionArea
                onClick={action.action}
                sx={{
                  '&:hover .MuiCardActionArea-focusHighlight': {
                    opacity: 0,
                  },
                }}
              >
                <CardContent sx={{ textAlign: 'center', maxWidth: '180px' }}>
                  <Box
                    sx={{
                      marginBottom: '13px',
                      marginInline: 'auto',
                      width: { xs: '48px', sm: '64px' },
                      height: { xs: '48px', sm: '64px' },
                      '& svg': {
                        maxWidth: '100%',
                        maxHeight: '100%',
                      },
                    }}
                  >
                    {action.icon}
                  </Box>
                  <Typography
                    gutterBottom
                    variant='h6'
                    component='h6'
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 700,
                      fontSize: { xs: '14px', sm: '16px' },
                      lineHeight: { xs: '157%', sm: '175%' },
                      letterSpacing: { xs: '.1px', sm: '.15px' },
                      color: isDark ? '#FFFFFF' : '#1A1A1A',
                      marginBottom: '8px',
                    }}
                  >
                    {action.title}
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: { xs: '14px', sm: '16px' },
                      lineHeight: { xs: '143%', sm: '150%' },
                      letterSpacing: '.15px',
                      color: isDark ? '#B6B6B6' : '#393939',
                    }}
                  >
                    {action.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
        {/* Contact Form Section */}
        <Box sx={{ marginBottom: { xs: '24px', sm: '48px' } }}>
          {isFormOpened && <ContactUsForm />}
        </Box>
        {/* FAQs Section */}
        <Box
          ref={targetFAQ}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: '16px', sm: '48px' },
            width: '100%',
            marginBottom: { xs: '24px', sm: '48px' },
          }}
        >
          <Typography
            variant='h5'
            component='h2'
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              textAlign: 'center',
              fontWeight: { xs: 700, sm: 600 },
              fontSize: { xs: 24, sm: 34 },
              lineHeight: { xs: '133%', sm: '124%' },
              color: isDark ? '#00FFFF' : '#005CAD',
            }}
          >
            Frequently Asked Questions
          </Typography>

          <Box sx={{ width: '100%' }}>
            {faqItems.map((item, index) => (
              <FaqAccordionItem
                key={index}
                question={item.question}
                answer={item.answer}
                isDark={isDark}
              />
            ))}
          </Box>
        </Box>

        {/* GetStarted Section */}
        <GetStarted />
      </Container>
    </Box>
  );
};

export default SupportPage;
